/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import { Component } from "react";

import Banner from "../components/banner";
import Newsletter from "../components/newsletter";
import OpenGraphHead from "../components/og-head";
import TwitterHead from "../components/twitter-head";
import { toAbsolutePageURL } from "../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class Terms extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/terms", market);
    const alt = "Lasting Smiles";
    const title = "Terms of Use · Lasting Smiles";
    const description = "Terms of use for the best all natural, organic, fair trade, sustainable, cruelty free, dermatologist recommended lip balm, chapstick, lip care, and lip treatments for healing, hydrating, and moisturizing your dry, chapped lips.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/terms", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/terms", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/terms", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/terms", market),
                    "name": "Terms of Use",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="terms"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="terms"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl">Terms of Use</h1>
            <p className="my-5">
              Last Updated: 3/7/2020
            </p>
            <p className="my-5">
              These Terms of Use (the "Terms") apply to your use of the website and domains owned by StartMark, SPC, including without limitation <a className="underline" href="https://www.lastingsmiles.org/">www.lastingsmiles.org</a> (the "Website").
              The Website is provided by StartMark, SPC, a Washington social purpose corporation ("StartMark," "We," "Us," or "Our").
            </p>
            <p className="my-5">
              Your use of the Website is subject to these Terms, together with any written agreements you enter into with Us, or other terms that We post with the Website ("Additional Terms").
              Additional Terms are incorporated by reference into these Terms.
            </p>
            <p className="my-5">
              By using the Website you agree to be bound by these Terms, so please read them carefully.
              Please don't use the Website if you don't agree to these Terms.
            </p>

            <h2 className="font-medium font-serif text-2xl">Acceptance of Terms of Use</h2>
            <p className="my-5">
              You affirm that you are at least 18 years of age, or that you have reviewed these Terms with your parent or guardian and that he or she agrees to these Terms on your behalf.
              If you are under 18, you may use the Website only with involvement of a parent or guardian.
            </p>

            <h2 className="font-medium font-serif text-2xl">Changes to these Terms</h2>
            <p className="my-5">
              We may change these Terms from time to time.
              If We do, We'll post the most recent version on Our site.
              You can view the latest Terms any time by clicking the "Terms of Use" link at the bottom of the applicable webpage.
              We encourage you to check for updates regularly.
              Your continued use of the Website means that you have agreed to the updated Terms.
            </p>

            <h2 className="font-medium font-serif text-2xl">Privacy</h2>
            <p className="my-5">
              Your privacy is important to Us.
              We have provided a Privacy Policy to explain what information about you that We collect, and how We use, share, and protect such information.
              By using the Website, you agree to Our collection and use of your information as described in the Privacy Policy.
              We encourage you to review the Privacy Policy to learn more.
              Please contact Us at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a> if you have any questions.
            </p>

            <h2 className="font-medium font-serif text-2xl">Electronic Communications</h2>
            <p className="my-5">
              When you send e-mails to Us, you are communicating with Us electronically.
              You consent to receive communications from Us electronically.
              We will communicate with you by e-mail or by posting notices on this Website.
              You agree that all agreements, notices, disclosures and other communications that We provide to you electronically satisfy any legal requirement that such communications be in writing.
            </p>

            <h2 className="font-medium font-serif text-2xl">Content Ownership</h2>
            <p className="my-5">
              The photos, images, graphics, text and content displayed on or used in connection with Our Website ("Content"), including without limitation, the design, expression, and arrangement of such Content, is Our proprietary property or is licensed to Us and is protected by United States and international copyright laws.
              Except as otherwise provided in these Terms, you are not permitted to copy, modify, display, license, create derivative works of, or otherwise exploit Our Content without Our written permission.
            </p>

            <h2 className="font-medium font-serif text-2xl">Trademarks</h2>
            <p className="my-5">
              The trademarks, graphics, logos, page headers, button icons, scripts, and service names included in or made available through this Website are trademarks or trade dress of StartMark in the U.S. and other countries, including without limitation STARTMARK, LASTING SMILES and USE YOUR LIPS SMILE FOR A CAUSE.
              StartMark's trademarks and trade dress may not be used in connection with any product or service that is not StartMark's, in any manner that is likely to cause confusion among customers, or in any manner that disparages or discredits StartMark.
            </p>

            <h2 className="font-medium font-serif text-2xl">Use of the Website</h2>
            <p className="my-5">
              You are permitted to use the Website for your own personal, non-commercial purposes.
              You agree that you will not use the Website for any unlawful purpose, in a way prohibited by the Terms, or in a manner that interferes with Our operation of, or any user's use and enjoyment of the Website.
              For example, when using Our Website you agree not to:
            </p>
            <ul className="list-disc my-5">
              <li>Use any hacking or cheating software or tools;</li>
              <li>Use any robot, iframe, spider, crawler, scraper or other automated means or interface not provided by Us to access the Website, including, without limitation, for the purpose of copying, extracting, aggregating, displaying, publishing or distributing any content or data made available via Website;</li>
              <li>Upload any files that may damage Our Website; or</li>
              <li>Attempt to modify any Website in way that We have not permitted.</li>
            </ul>
            <p className="my-5">
              If you would like to report a violation of these Terms, please contact Us at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>.
            </p>

            <h2 className="font-medium font-serif text-2xl">Registration and Security</h2>
            <p className="my-5">
              Our Website allows you to create an account with Us, with a user ID and password.
              You agree that you will provide only accurate, complete, and up-to-date information.
              You are responsible for ensuring the confidentiality of your password and for restricting access to your computer, and you agree to accept responsibility for all activities that occur under your account or password.
              You are required to let Us know immediately if you suspect that someone is using your account without your permission.
              We are not responsible for any loss or damage arising from your failure to comply with these obligations.
            </p>

            <h2 className="font-medium font-serif text-2xl">Customer Service</h2>
            <p className="my-5">
              If you have any questions on your orders, such as payment information or status of any order, if you need to return products, or have a question about Our products, please contact us by e-mail at <a href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>.
            </p>

            <h2 className="font-medium font-serif text-2xl">Returns</h2>
            <p className="my-5">
              All of our products are 100% guaranteed.
              If for any reason you are not satisfied with your purchase, you may return your item for an exchange (shipping charges apply for exchanges outside the United States) or full refund using the return shipping label included with your order.
              When sending a return, please include a note with your contact information, reason for the return and desired outcome.
              Returns should be mailed to us at the following address:
            </p>
            <pre className="my-5">
              StartMark, SPC<br />
              Attn.: Returns<br />
              PO Box 15944<br />
              Seattle, WA 98115<br />
              E-mail: info@lastingsmiles.org
            </pre>

            <h2 className="font-medium font-serif text-2xl">Copyright Usage/Infringement Notices</h2>
            <p className="my-5">
              If you believe that your copyrighted materials are being infringed on Our Website, please contact Us at:
            </p>
            <pre className="my-5">
              StartMark, SPC<br />
              Attn.: Copyright Agent<br />
              PO Box 15944<br />
              Seattle, WA 98115<br />
              E-mail: info@lastingsmiles.org
            </pre>
            <h3 className="font-medium font-serif text-xl">Links to Third-Party Websites and Content</h3>
            <p className="my-5">
              Our Website may include links to websites or content owned or operated by third parties.
              Some third party sites may collect data or solicit personal information from you.
              StartMark does not own or control such sites and is not responsible for their content or actions.
              We suggest that you read the terms and conditions and privacy policies of any website or content linked to Our Website to learn about the third party's practices.
            </p>
            <h3 className="font-medium font-serif text-xl">Modifying/Terminating the Website</h3>
            <p className="my-5">
              We regularly update and change Our Website.
              We may also remove or modify features, functionalities, or remove or discontinue a product.
              You agree that We can suspend or terminate your right to access Our Website at any time for any reason.
              If you learn that We have suspended or terminated your right to Our Website, you agree not to try to access the Website again without Our permission.
            </p>
            <h3 className="font-medium font-serif text-xl">Disputes and Indemnification</h3>
            <p className="my-5">
              PLEASE READ THIS SECTION CAREFULLY BECAUSE IT AFFECTS YOUR LEGAL RIGHTS.
              <br />
              We hope that you won't have any problems when you use of Our Website.
              If you do, We ask that you contact StartMark customer service at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>.
              However, if there is a problem that We can't fix, and you have any claim, cause of action, or dispute (a "claim") involving Us (including Our officers, directors, affiliates, employees, agents, successors, and assigns) that arises out of or relates to these Terms, you must bring that claim within one year, unless a longer period is required by law.
              If you don't, you'll lose the right to bring the claim.
              The laws of the State of Washington (notwithstanding any conflict of laws rules) will apply to these Terms and your use of Our Website.
              You agree to the exclusive jurisdiction of the state and federal courts located in King County, Washington.
              You may not assign your rights under these Terms or otherwise assign to anyone else any claims that you may have regarding the Website.
              If you or anyone else brings a claim arising out of or relating to your use of the Website, you agree to indemnify Us and hold Us harmless from any claims, losses, expenses, damages, and costs (including attorneys' fees) that We have in connection with any such claim.
              If you have a dispute with someone else relating to StartMark, you release Us from any liability arising out of or relating to that dispute.
            </p>
            <h3 className="font-medium font-serif text-xl">Disclaimer of Warranties</h3>
            <p className="my-5">
              WE PROVIDE OUR WEBSITE "AS IS," WITHOUT ANY REPRESENTATION OR WARRANTY OF ANY KIND.
              STARTMARK DISCLAIMS TO THE MAXIMUM EXTENT PERMITTED BY LAW ALL REPRESENTATIONS AND WARRANTIES PERTAINING TO OUR WEBSITE.
              WITHOUT LIMITING THE GENERALITY OF THE STATEMENT ABOVE, WE DISCLAIM TO THE MAXIMUM EXTENT PERMITTED BY LAW ANY AND ALL (A) WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE; (B) WARRANTIES AGAINST INFRINGEMENT OF ANY THIRD-PARTY INTELLECTUAL PROPERTY OR PROPRIETARY RIGHTS; (C) WARRANTIES RELATING TO THE TRANSMISSION OR DELIVERY OF THE WEBSITE; (D) WARRANTIES RELATING TO THE ACCURACY, RELIABILITY, CORRECTNESS, OR COMPLETENESS OF DATA MADE AVAILABLE ON THE WEBSITE; AND (E) WARRANTIES OTHERWISE RELATING TO PERFORMANCE, NONPERFORMANCE, OR OTHER ACTS OR OMISSIONS BY STARTMARK OR ANY THIRD PARTY.
              FURTHER, THERE IS NO WARRANTY THAT THE WEBSITE WILL MEET YOUR NEEDS OR REQUIREMENTS OR THE NEEDS OR REQUIREMENTS OF ANY OTHER PERSON.
              WE MAKE NO WARRANTIES OR REPRESENTATIONS, EXPRESS OR IMPLIED, (X) THAT THE INFORMATION PROVIDED THROUGH THE WEBSITE WILL BE FREE FROM ERROR, OMISSION, INTERRUPTION, DEFECT, OR DELAY IN OPERATION, OR FROM TECHNICAL INACCURACIES OR TYPOGRAPHICAL ERRORS; (Y) THAT THE WEBSITE WILL BE AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; OR (Z) THAT THE WEBSITE IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
            <h3 className="font-medium font-serif text-xl">Limitation of Liability</h3>
            <p className="my-5">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR ANY DAMAGE ARISING OUT OF OR RELATING TO YOUR ACCESS, USE, MISUSE, OR INABILITY TO USE THE WEBSITE OR IN CONNECTION WITH ANY FAILURE OF PERFORMANCE, ERROR, OMISSION, INTERRUPTION, DEFECT, DELAY IN OPERATION OR TRANSMISSION, COMPUTER VIRUS, OR LINE OR SYSTEM FAILURE.
              WE WILL NOT BE LIABLE FOR ANY SPECIAL OR CONSEQUENTIAL DAMAGES, INCLUDING LOST PROFITS, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF THOSE DAMAGES.
              OUR AGGREGATE LIABILITY TO YOU IN CONNECTION WITH ANY CLAIMS ARISING OUT OF OR RELATING TO THE WEBSITE WILL BE LIMITED TO THE GREATER OF $100 OR THE TOTAL AMOUNT THAT YOU PAID TO US OVER THE PAST SIX (6) MONTHS.
              If a law restricts Our ability to limit liability, the limitations listed above may not apply to you.
              In that case, We limit Our liability to the greatest extent permitted by applicable law.
            </p>
            <h3 className="font-medium font-serif text-xl">Miscellaneous</h3>
            <p className="my-5">
              If any part of these Terms is determined to be unlawful, void or for any reason unenforceable, then that part will be severed from these Terms, and the rest of the Terms will remain intact.
              If We do not enforce any provision of these Terms, that will not be considered a waiver of Our rights.
              Any waiver of these Terms must be obtained in a written document signed by an authorized representative of StartMark.
            </p>
          </div>
        </section>
      </>
    );
  }
}
